import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
// ✅ Import side-effect : enregistre l'intercepteur axios qui injecte auth_token
import '@/lib/api';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Paramètres ──────────────────────────────────────────────────────────────
const CHECK_INTERVAL_MS = 30 * 1000;          // Vérifier toutes les 30 secondes
const HEURE_DECLENCHEMENT = 16;               // 16h (4 PM)
const COUNTDOWN_SECONDS = 10 * 60;            // 10 minutes pour répondre (demande utilisateur 2026-05-06)
const DEFAULT_SNOOZE_MINUTES = 60;            // Délai par défaut choisi si l'utilisateur ferme sans choisir

// Clé localStorage pour persister le "prochain rappel" entre rafraîchissements.
// Avant : un useRef était utilisé — réinitialisé à chaque refresh, ce qui forçait
// la modal à revenir immédiatement après le F5.
const LS_KEY_NEXT_PROMPT = 'autoPunchOut__nextPromptAt';

const DEBUG = true;
const log = (...args) => { if (DEBUG) console.log('[AutoPunchOut]', ...args); };

const getNextPromptAt = () => {
  try {
    const v = localStorage.getItem(LS_KEY_NEXT_PROMPT);
    return v ? parseInt(v, 10) : null;
  } catch { return null; }
};
const setNextPromptAt = (ts) => {
  try {
    if (ts === null) localStorage.removeItem(LS_KEY_NEXT_PROMPT);
    else localStorage.setItem(LS_KEY_NEXT_PROMPT, String(ts));
  } catch { /* graceful */ }
};

/**
 * Composant qui surveille les propriétaires punchés après 16h et les dépunch
 * automatiquement s'ils ne répondent pas dans les 10 minutes.
 *
 * Quand l'utilisateur confirme être toujours là, il CHOISIT le délai avant
 * la prochaine demande (30 min / 1 h / 2 h). Ce délai est persisté dans
 * localStorage → refresh ne re-déclenche pas la modal immédiatement.
 */
export default function AutoPunchOutMonitor({ user }) {
  const [showPrompt, setShowPrompt] = useState(false);     // Modal "Êtes-vous toujours là ?"
  const [showSnoozeChoice, setShowSnoozeChoice] = useState(false); // Modal "Choisir délai"
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  // Refs pour éviter les closures stale dans setInterval
  const showPromptRef = useRef(false);
  const isPollingRef = useRef(false);

  const isOwner = estProprietaire(user); // Cat H — feature auto-punch-out at 4pm réservée par nature au propriétaire

  useEffect(() => { showPromptRef.current = showPrompt; }, [showPrompt]);

  const checkPunchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/punch/status`);
      return !!res.data?.is_punched_in;
    } catch (err) {
      log('Erreur vérification punch:', err?.response?.status || err?.message || err);
      return false;
    }
  }, []);

  // Dépunch automatique
  const autoPunchOut = useCallback(async () => {
    try {
      let body = {};
      try {
        const { requestGPS } = await import('../lib/gpsUtils');
        const gps = await requestGPS({ required: false, timeout: 5000 });
        if (gps && gps.latitude && gps.longitude) {
          body = { latitude: gps.latitude, longitude: gps.longitude };
        }
      } catch (gpsErr) {
        log('GPS indisponible pour auto-out:', gpsErr?.message || gpsErr);
      }

      await axios.post(`${API_URL}/api/punch/auto-out`, body);
      toast.success('Vous avez été dépunché automatiquement (pas de réponse)');
    } catch (err) {
      log('Erreur auto punch-out:', err?.response?.data || err?.message || err);
      toast.error('Erreur lors du dépunch automatique');
    } finally {
      setShowPrompt(false);
      setShowSnoozeChoice(false);
      showPromptRef.current = false;
      // Nettoyer le prochain rappel : la personne n'est plus punchée.
      setNextPromptAt(null);
    }
  }, []);

  // Décide si on doit afficher la modal
  const maybePromptUser = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      const now = Date.now();
      const hours = new Date(now).getHours();

      if (hours < HEURE_DECLENCHEMENT) return;
      if (showPromptRef.current) return;

      // Respecter le délai choisi par l'utilisateur (persisté entre refreshs)
      const nextAt = getNextPromptAt();
      if (nextAt && now < nextAt) {
        log('Next prompt in', Math.round((nextAt - now) / 60000), 'min — skip');
        return;
      }

      const punched = await checkPunchStatus();
      if (!punched) {
        // Si l'utilisateur n'est plus punché, on nettoie l'horaire pour ne
        // pas bloquer une future session.
        if (nextAt) setNextPromptAt(null);
        return;
      }

      // Afficher la modal "Êtes-vous toujours là ?"
      setCountdown(COUNTDOWN_SECONDS);
      setShowPrompt(true);
      showPromptRef.current = true;
    } finally {
      isPollingRef.current = false;
    }
  }, [checkPunchStatus]);

  // Polling principal — ne dépend QUE de isOwner pour éviter les resets intempestifs
  useEffect(() => {
    if (!isOwner) return;
    log('Monitor démarré pour propriétaire');
    maybePromptUser();
    const id = setInterval(maybePromptUser, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOwner, maybePromptUser]);

  // Countdown quand la modal "Êtes-vous toujours là ?" est ouverte
  useEffect(() => {
    if (!showPrompt) return;
    if (countdown <= 0) {
      autoPunchOut();
      return;
    }
    const t = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [showPrompt, countdown, autoPunchOut]);

  // L'utilisateur confirme être là → ouvrir le modal de choix du délai
  const handleStillHere = () => {
    setShowPrompt(false);
    showPromptRef.current = false;
    setShowSnoozeChoice(true);
  };

  // L'utilisateur choisit un délai de rappel
  const handleSnoozeChoice = (minutes) => {
    const next = Date.now() + minutes * 60 * 1000;
    setNextPromptAt(next);
    setShowSnoozeChoice(false);
    const label = minutes >= 60 ? `${minutes / 60}h` : `${minutes} min`;
    toast.success(`Merci ! On vous redemandera dans ${label}.`);
  };

  // L'utilisateur ferme le choix du délai sans choisir → appliquer 1h par défaut
  const handleCloseSnoozeChoice = (open) => {
    if (!open) {
      handleSnoozeChoice(DEFAULT_SNOOZE_MINUTES);
    }
  };

  const handlePunchOut = async () => { await autoPunchOut(); };

  if (!isOwner) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <>
      {/* ── Modal 1 : Êtes-vous toujours là ? ── */}
      <Dialog open={showPrompt} onOpenChange={(open) => { if (!open) handlePunchOut(); }}>
        <DialogContent className="sm:max-w-md" data-testid="auto-punchout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Êtes-vous toujours là ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Il est maintenant <strong>{new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</strong>.
              Confirmez que vous êtes toujours au travail pour rester punché.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-center gap-3">
              <Clock className="w-6 h-6 text-orange-600" />
              <div className="text-center">
                <p className="text-xs text-orange-600 mb-1">Temps restant</p>
                <p className={`text-3xl font-bold ${countdown <= 60 ? 'text-red-600' : 'text-orange-700'}`}>
                  {minutes}:{seconds.toString().padStart(2, '0')}
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                ⚠️ Si vous ne répondez pas, vous serez <strong>dépunché automatiquement</strong> dans {minutes} minute{minutes > 1 ? 's' : ''}.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleStillHere}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="auto-punchout-still-here"
              >
                ✅ Oui, je suis encore là
              </Button>
              <Button
                onClick={handlePunchOut}
                variant="outline"
                className="flex-1"
                data-testid="auto-punchout-punch-out"
              >
                ❌ Non, me dépuncher
              </Button>
            </div>
            <p className="text-xs text-center text-slate-400 italic">
              Si vous cliquez "Oui", vous pourrez choisir quand on vous redemandera.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal 2 : Choisir le délai avant la prochaine demande ── */}
      <Dialog open={showSnoozeChoice} onOpenChange={handleCloseSnoozeChoice}>
        <DialogContent className="sm:max-w-md" data-testid="auto-punchout-snooze-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-emerald-600" />
              Quand voulez-vous qu'on vous redemande ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pour éviter les rappels trop fréquents, choisissez un délai avant la prochaine vérification :
            </p>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                onClick={() => handleSnoozeChoice(30)}
                variant="outline"
                className="h-14 text-base hover:bg-emerald-50 hover:border-emerald-300"
                data-testid="auto-punchout-snooze-30min"
              >
                Dans 30 min
              </Button>
              <Button
                onClick={() => handleSnoozeChoice(60)}
                className="h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="auto-punchout-snooze-1h"
              >
                Dans 1 h
              </Button>
              <Button
                onClick={() => handleSnoozeChoice(90)}
                variant="outline"
                className="h-14 text-base hover:bg-emerald-50 hover:border-emerald-300"
                data-testid="auto-punchout-snooze-90min"
              >
                Dans 1 h 30
              </Button>
              <Button
                onClick={() => handleSnoozeChoice(120)}
                variant="outline"
                className="h-14 text-base hover:bg-emerald-50 hover:border-emerald-300"
                data-testid="auto-punchout-snooze-2h"
              >
                Dans 2 h
              </Button>
            </div>
            <p className="text-xs text-center text-slate-400 italic pt-2">
              Vous pouvez refresh la page sans que la fenêtre ne revienne avant le délai choisi.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
